import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  LoaderButtonComponent,
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { QRCodeComponent } from 'angularx-qrcode';
import { TotpSecret } from 'firebase/auth';
import { AuthService } from '../../auth.service';
import { getAuthErrorMessage } from '../auth-error-messages';
import { MultifactorService } from '../multifactor.service';
import { IMultifactorEnrolmentResult } from '../multifactor.types';

interface IVerifyForm {
  code: string;
}

@Component({
  selector: 'pt-totp-enrol',
  templateUrl: './totp-enrol.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    LoaderButtonComponent,
    QRCodeComponent,
  ],
})
export class TotpEnrolComponent {
  private _multifactorService = inject(MultifactorService);

  auth = inject(AuthService);
  enrolled = output<IMultifactorEnrolmentResult>();
  totpSecret: TotpSecret | undefined;
  qrCodeUrl = '';
  isLoading = signal(false);
  errorMessage = '';

  codeForm = new TypedFormGroup<IVerifyForm>({
    code: new TypedFormControl<string>('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
    ]),
  });

  async generateQrCode(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage = '';
    try {
      const { secret, qrCodeUrl } = await this._multifactorService.enrolTotp();
      this.totpSecret = secret;
      this.qrCodeUrl = qrCodeUrl;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MFA TOTP generate QR code failed:', error);
      this.errorMessage = getAuthErrorMessage(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  async confirmEnrolment(): Promise<void> {
    if (!this.codeForm.valid || !this.totpSecret) {
      return;
    }
    this.isLoading.set(true);
    this.errorMessage = '';
    try {
      const result = await this._multifactorService.confirmTotpEnrolment(
        this.totpSecret,
        this.codeForm.value.code
      );
      this.enrolled.emit(result);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MFA TOTP confirm enrolment failed:', error);
      this.errorMessage = getAuthErrorMessage(error);
    } finally {
      this.isLoading.set(false);
    }
  }
}
